import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { delphiService, type PromptTemplate } from '../../services/delphiApi';

const promptTypeOptions = [
  { value: '', label: 'All Types' },
  { value: 'translation_hi', label: 'Hindi Translation' },
  { value: 'match_generation', label: 'Match Generation' },
  { value: 'seo', label: 'SEO Generation' },
  { value: 'social', label: 'Social Copy' },
  { value: 'visual_prompt', label: 'Visual Prompt / Banner' },
];

const columns: GridColDef[] = [
  { field: 'name', headerName: 'Name', width: 220 },
  { field: 'key', headerName: 'Key', width: 160 },
  {
    field: 'prompt_type',
    headerName: 'Type',
    width: 150,
    renderCell: (params) => <Chip size="small" label={params.value} />,
  },
  {
    field: 'is_default',
    headerName: 'Default',
    width: 110,
    renderCell: (params) => (params.value ? <Chip label="Default" size="small" color="success" /> : null),
  },
  {
    field: 'is_active',
    headerName: 'Active',
    width: 110,
    renderCell: (params) => (params.value ? <Chip label="Active" size="small" color="primary" /> : <Chip label="Inactive" size="small" />),
  },
  {
    field: 'updated_at',
    headerName: 'Updated',
    width: 180,
    renderCell: (params) => <Typography variant="body2">{new Date(params.value as string).toLocaleString()}</Typography>,
  },
  {
    field: 'description',
    headerName: 'Description',
    flex: 1,
    minWidth: 200,
  },
];

const PromptTemplates: React.FC = () => {
  const [prompts, setPrompts] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [promptType, setPromptType] = useState<string>('');
  const [selectedPrompt, setSelectedPrompt] = useState<PromptTemplate | null>(null);

  const fetchPrompts = async (type?: string) => {
    setLoading(true);
    try {
      const data = await delphiService.listPrompts(type ? { prompt_type: type } : undefined);
      setPrompts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  useEffect(() => {
    fetchPrompts(promptType || undefined);
  }, [promptType]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Prompt Templates
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        View available prompt templates for translation, SEO, social, visual prompts, and match generation.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Prompt Type</InputLabel>
          <Select
            value={promptType}
            label="Prompt Type"
            onChange={(e) => setPromptType(e.target.value)}
          >
            {promptTypeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Paper sx={{ width: '100%', position: 'relative' }}>
        <DataGrid
          rows={prompts}
          columns={columns}
          getRowId={(row) => row.id}
          density="compact"
          loading={loading}
          disableRowSelectionOnClick
          initialState={{
            sorting: { sortModel: [{ field: 'updated_at', sort: 'desc' }] },
          }}
          onRowClick={(params) => setSelectedPrompt(params.row)}
        />
        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        )}
      </Paper>

      <Dialog open={!!selectedPrompt} onClose={() => setSelectedPrompt(null)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedPrompt?.name}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            <Typography variant="body2" color="text.secondary">
              Key: {selectedPrompt?.key} • Type: {selectedPrompt?.prompt_type} • Updated: {selectedPrompt ? new Date(selectedPrompt.updated_at).toLocaleString() : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedPrompt?.description}
            </Typography>
          </DialogContentText>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            System Prompt
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#fafafa' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {selectedPrompt?.system_prompt || '—'}
            </Typography>
          </Paper>
          <Typography variant="subtitle2" gutterBottom>
            User Prompt
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fafafa' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {selectedPrompt?.user_prompt || '—'}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedPrompt(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromptTemplates;

